/**
 * The Lab's structured comparisons of two claims, read where the claims are.
 *
 * "Structure disagreement" in the Research Lab's Tests tab puts two claims to
 * the selected model, which extracts each one's scope — population, exposure,
 * outcome, measurement, horizon, design and the rest — and closed rules in
 * `core/contradiction.ts` then decide whether the pair genuinely conflicts, only
 * looks as if it does, or cannot be told apart on what was recorded, and rank the
 * tests proposed to separate them. A usability study found it the most useful
 * thing in the app and found nobody reaching it. The node card's "Why these
 * disagree" and the report's section on where findings disagree read it from
 * here.
 *
 * READ-ONLY, and from where the Lab stores it: `research.contradictionAnalyses`,
 * written by `compareClaimPair` in `ui/ResearchLab.tsx` from either door. No
 * comparison is made, copied or re-classified here, so the card, the report and
 * the Tests tab cannot come to hold three versions of one verdict.
 *
 * Pure over a `Session`, so the report can use it too. Its one runtime import is
 * the canonical JSON the Findings tab compares a stored scope with, so the two
 * cannot disagree about whether a comparison's inputs have moved.
 */

import type {
  ContradictionAnalysis,
  ContradictionVerdict,
  DiscriminatingTest,
  ScopeDifference,
  ScopeDimension,
} from './contradiction'
import { canonicalSemanticJson } from './semanticGit'
import type { ReasonNode, Session } from './types'

/**
 * The pair a node's "Why do these disagree?" press compares: an objection and
 * the claim it hangs off, or null.
 *
 * Only a contradiction node carries the press, because only there does the graph
 * itself say which other claim this one disagrees with — the engine compares TWO
 * CLAIMS, and a mixed reading's sources disagreeing with each other is not two
 * claims. Never against the seed: under a question or a goal the root is the
 * reader's prompt, not a claim with a scope to extract, which is also why the
 * coach's `contra` cue never anchors there. The claim is first, the objection
 * second — the order the Lab's two selects would put them in.
 */
export function disagreePairOf(
  session: Session,
  node: ReasonNode,
): { leftId: string; rightId: string } | null {
  if (node.kind !== 'contradiction' || node.parentId === null) return null
  const parent = session.nodes[node.parentId]
  if (!parent || parent.parentId === null) return null
  return { leftId: parent.id, rightId: node.id }
}

/**
 * Every stored comparison this node is in, the newest per counterpart, newest
 * first — and only where the other claim is still on the graph.
 *
 * Newest per PAIR, the same pick the Lab's Findings tab makes for one pair
 * (`FindingComparison`): the Tests tab keeps every run on record, and a card that
 * showed each of them would show a pair's history as several disagreements.
 */
export function comparisonsFor(session: Session, nodeId: string): ContradictionAnalysis[] {
  const newest = new Map<string, ContradictionAnalysis>()
  for (const row of session.research?.contradictionAnalyses ?? []) {
    const other =
      row.left.nodeId === nodeId
        ? row.right.nodeId
        : row.right.nodeId === nodeId
          ? row.left.nodeId
          : null
    if (!other || other === nodeId || !session.nodes[other]) continue
    const held = newest.get(other)
    if (
      !held ||
      row.createdAt > held.createdAt ||
      (row.createdAt === held.createdAt && row.id < held.id)
    )
      newest.set(other, row)
  }
  return [...newest.values()].sort((a, b) => b.createdAt - a.createdAt || (a.id < b.id ? -1 : 1))
}

/** Every stored pair, the newest comparison of each, both claims still on the
 *  graph — for the report, which lists each pair once. */
export function latestComparisons(session: Session): ContradictionAnalysis[] {
  const newest = new Map<string, ContradictionAnalysis>()
  for (const row of session.research?.contradictionAnalyses ?? []) {
    const a = row.left.nodeId
    const b = row.right.nodeId
    if (a === b || !session.nodes[a] || !session.nodes[b]) continue
    const key = a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`
    const held = newest.get(key)
    if (
      !held ||
      row.createdAt > held.createdAt ||
      (row.createdAt === held.createdAt && row.id < held.id)
    )
      newest.set(key, row)
  }
  return [...newest.values()].sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1))
}

/**
 * Whether what was compared is no longer what is on the graph: either claim's
 * wording has changed, or its current scope record differs from the scope the
 * comparison classified. The Findings tab's condition for "Earlier comparison —
 * inputs have changed", asked of the same stored fields.
 */
export function comparisonStale(session: Session, analysis: ContradictionAnalysis): boolean {
  return [analysis.left, analysis.right].some((recorded) => {
    const current = session.research?.claimScopes.find((row) => row.nodeId === recorded.nodeId)
    return (
      session.nodes[recorded.nodeId]?.claim !== recorded.claim ||
      (!!current && canonicalSemanticJson(current.scope) !== canonicalSemanticJson(recorded.scope))
    )
  })
}

/**
 * What each verdict means, in one sentence, from the rule that reaches it
 * (`classifyContradiction`). A sentence rather than a status word: the verdict's
 * own word is `COMPARISON_VERDICT_LABEL`, and this is what a reader who does not
 * know the Lab's vocabulary needs beside it.
 */
export const VERDICT_GLOSS: Record<ContradictionVerdict, string> = {
  genuine:
    'every recorded scope matches and the two point opposite ways, so as stated they cannot both hold.',
  'scope-mismatch':
    'they differ on what is measured, or on the case it is measured in, so both can hold — each of its own case.',
  compatible: 'on the recorded scopes their directions do not conflict.',
  underdetermined:
    'too much of the scope was left unspecified, or a direction is mixed, to say whether they conflict.',
}

/** A scope dimension in words. Field names, not status words: what was compared. */
export const DIMENSION_LABEL: Record<ScopeDimension, string> = {
  population: 'Population',
  intervention: 'Intervention',
  exposure: 'Exposure',
  comparator: 'Comparator',
  outcome: 'Outcome',
  measurement: 'Measurement',
  timeHorizon: 'Time horizon',
  studyDesign: 'Study design',
  definition: 'Definition',
  causalMode: 'Causal mode',
  assumptions: 'Assumptions',
}

/**
 * The rows that say WHY: the dimensions the rules found the two claims differing
 * on — a different question (outcome, measurement, definition), or a moderator
 * that can make both effects true of different cases. `same` explains nothing,
 * and `unknown` is a field one side left empty, which the verdict already counts
 * as uncertainty rather than difference.
 */
export function scopeReasons(analysis: ContradictionAnalysis): ScopeDifference[] {
  return analysis.differences.filter(
    (row) => row.kind === 'different-question' || row.kind === 'moderator',
  )
}

/** How many dimensions one side or both left unspecified — the reason an
 *  `underdetermined` verdict is one. */
export const unspecifiedCount = (analysis: ContradictionAnalysis): number =>
  analysis.differences.filter((row) => row.kind === 'unknown').length

/**
 * The two cheapest proposals that could separate the pair, in the stored order —
 * which IS the ranking: `completeContradictionAnalysis` sorts by
 * `rankDiscriminatingTests` when a comparison is made and again whenever a person
 * records a test's real cost in the Lab, so reading the head is reading the rank.
 */
export const topTests = (analysis: ContradictionAnalysis, n = 2): DiscriminatingTest[] =>
  analysis.tests.slice(0, n)
